import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import './OrganizationDashboard.css';
import {
  FaUserFriends,
  FaFileInvoiceDollar,
  FaDollarSign,
  FaCoins,
  FaCog,
  FaIndustry,
  FaEnvelope,
  FaHashtag,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimes,
  FaClock,
} from 'react-icons/fa';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function OrganizationDashboard({ org, onOrgUpdated }) {
  const [employees, setEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // --- Organization Settings (edit org) flow ---
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [orgForm, setOrgForm] = useState({
    name: '',
    industry: '',
    email: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    business_number: '',
  });

    const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, payRes] = await Promise.all([
        apiFetch(`/organizations/${org.id}/employees`, org.tenantId),
        apiFetch(`/organizations/${org.id}/payroll-records`, org.tenantId),
      ]);

      const [empData, payData] = [empRes || [], payRes || []];

      setEmployees(empData);
      setPayrollRecords(payData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [org?.id, org?.tenantId]);

  useEffect(() => {
    if (org) {
      setBannerDismissed(false);
      fetchDashboardData();
    }
  }, [org, fetchDashboardData]);


  const openEditModal = () => {
    setOrgForm({
      name: org?.name || '',
      industry: org?.industry || '',
      email: org?.email || '',
      address: org?.address || '',
      city: org?.city || '',
      province: org?.province || '',
      postal_code: org?.postal_code || '',
      business_number: org?.business_number || '',
    });
    setSaveError(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    if (savingOrg) return;
    setShowEditModal(false);
    setSaveError(null);
  };

  const handleOrgFormChange = (field) => (e) => {
    const value = e.target.value;
    setOrgForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveOrg = async (e) => {
    e.preventDefault();
    setSavingOrg(true);
    setSaveError(null);
    try {
      await apiFetch(`/organizations/${org.id}`, org.tenantId, {
        method: 'PUT',
        body: orgForm,
      });

      // The backend returns a success message, not the full object.
      // We pass back the form state which is the most up-to-date version.
      if (typeof onOrgUpdated === 'function') {
        onOrgUpdated({ ...org, ...orgForm });
      }
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating organization:', error);
      setSaveError(error.message || 'Unable to save organization settings. Please try again.');
    } finally {
      setSavingOrg(false);
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    if (!showEditModal) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeEditModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditModal, savingOrg]);

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown Employee';
  };

  const formatCurrency = (value) => {
    return (value || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    });
  };

  const totalPayroll = useMemo(
    () => payrollRecords.reduce((sum, p) => sum + (p.net_pay || 0), 0),
    [payrollRecords]
  );

  const avgNetPay = payrollRecords.length > 0 ? totalPayroll / payrollRecords.length : 0;

  // Aggregate net pay by month for the current calendar year, from real records only.
  const currentYear = new Date().getFullYear();
  const monthlyTotals = useMemo(() => {
    const buckets = new Array(12).fill(0);
    payrollRecords.forEach(r => {
      if (!r.pay_date) return;
      const d = new Date(r.pay_date);
      if (d.getFullYear() === currentYear) {
        buckets[d.getMonth()] += (r.net_pay || 0);
      }
    });
    return buckets;
  }, [payrollRecords, currentYear]);

  const maxMonthly = Math.max(...monthlyTotals, 1);

  const recentRecords = useMemo(() => {
    return [...payrollRecords]
      .filter(r => r.pay_date)
      .sort((a, b) => new Date(b.pay_date) - new Date(a.pay_date))
      .slice(0, 6);
  }, [payrollRecords]);

  const draftRecords = useMemo(() => {
    if (!Array.isArray(payrollRecords)) return [];
    return payrollRecords.filter(r => (r.status || 'Draft').toLowerCase() === 'draft');
  }, [payrollRecords]);

  const finalRecords = useMemo(() => {
    if (!Array.isArray(payrollRecords)) return [];
    return payrollRecords.filter(r => (r.status || '').toLowerCase() === 'final');
  }, [payrollRecords]);

  const hasDraftRecords = draftRecords.length > 0;

  const businessNumberConfigured = Boolean(org?.business_number);

  if (loading) {
    return (
      <div className="op-dashboard container-fluid py-5 d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="op-spinner mb-3"></div>
        <h5 className="fw-medium" style={{ color: '#5b6472' }}>Loading organization data…</h5>
      </div>
    );
  }

  return (
    <div className="op-dashboard container-fluid py-3 py-md-4 px-3 px-md-4">

      {/* Draft Payroll Records Alert Banner */}
      {hasDraftRecords && !bannerDismissed && (
        <div className="op-draft-banner" role="alert">
          <div className="op-draft-banner-content">
            <span className="op-draft-banner-icon-wrapper" aria-hidden="true">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="op-draft-banner-icon"
              >
                <path
                  d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86L7.86 2z"
                  fill="#ffffff"
                />
                <rect x="6.5" y="10.75" width="11" height="2.5" rx="1.25" fill="#ba1724" />
              </svg>
            </span>
            <span className="op-draft-banner-text">
              {draftRecords.length === 1
                ? 'There is 1 draft payroll record pending review. '
                : `There are ${draftRecords.length} draft payroll records pending review. `}
              <Link to="/payroll-records" className="op-draft-banner-link">
                Please review and finalize them.
              </Link>
            </span>
          </div>
          <button
            type="button"
            className="op-draft-banner-close"
            onClick={() => setBannerDismissed(true)}
            aria-label="Dismiss notification"
            title="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h2 className="op-page-title mb-0">Hi, {org?.name || 'Organization'}</h2>
            <span className="badge rounded-pill px-3 py-1" style={{ background: 'var(--gold-soft)', color: '#8a6a24', border: '1px solid rgba(200, 155, 60, 0.35)', fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: '0.78rem', letterSpacing: '0.3px', textTransform: 'uppercase' }}>Active</span>
          </div>
          <p className="op-subtitle mb-0">High-level overview and metrics for your payroll operations.</p>
        </div>
        <button
          type="button"
          className="btn op-settings-btn shadow-sm px-4 d-flex align-items-center justify-content-center gap-2 flex-shrink-0"
          onClick={openEditModal}
        >
          <FaCog /> Organization Settings
        </button>
      </div>

      {/* Key Metrics Row */}
      <div className="row g-3 g-md-4 mb-4">
        <div className="col-6 col-md-4 col-xl">
          <div className="op-card op-metric-card h-100 p-3 p-md-4" style={{ '--accent': 'var(--blue)' }}>
            <div className="op-metric-icon mb-3" style={{ background: 'var(--blue-soft)', color: 'var(--blue)' }}>
              <FaUserFriends size={18} />
            </div>
            <h6 className="op-metric-label mb-1">Active Employees</h6>
            <h2 className="op-metric-value mb-0">{employees.length}</h2>
            <p className="op-metric-caption mb-0 mt-2">Currently registered in system</p>
          </div>
        </div>

        <div className="col-6 col-md-4 col-xl">
          <div className="op-card op-metric-card h-100 p-3 p-md-4" style={{ '--accent': 'var(--gold)' }}>
            <div className="op-metric-icon mb-3" style={{ background: 'var(--gold-soft)', color: '#8a6a24' }}>
              <FaFileInvoiceDollar size={18} />
            </div>
            <h6 className="op-metric-label mb-1">Processed Records</h6>
            <h2 className="op-metric-value mb-0">{finalRecords.length}</h2>
            <p className="op-metric-caption mb-0 mt-2">Finalized pay runs</p>
          </div>
        </div>

        <div className="col-6 col-md-4 col-xl">
          <Link to="/payroll-records" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div
              className="op-card op-metric-card h-100 p-3 p-md-4"
              style={{
                '--accent': draftRecords.length > 0 ? '#ba1724' : 'var(--amber)',
                cursor: 'pointer',
              }}
            >
              <div
                className="op-metric-icon mb-3"
                style={{
                  background: draftRecords.length > 0 ? 'rgba(186, 23, 36, 0.1)' : 'var(--amber-soft)',
                  color: draftRecords.length > 0 ? '#ba1724' : 'var(--amber)',
                }}
              >
                <FaClock size={18} />
              </div>
              <h6 className="op-metric-label mb-1">Pending Records</h6>
              <h2
                className="op-metric-value mb-0"
                style={{ color: draftRecords.length > 0 ? '#ba1724' : 'inherit' }}
              >
                {draftRecords.length}
              </h2>
              <p className="op-metric-caption mb-0 mt-2">
                {draftRecords.length > 0 ? 'Draft runs awaiting review' : 'No pending drafts'}
              </p>
            </div>
          </Link>
        </div>

        <div className="col-6 col-md-4 col-xl">
          <div className="op-card op-metric-card h-100 p-3 p-md-4" style={{ '--accent': 'var(--emerald)' }}>
            <div className="op-metric-icon mb-3" style={{ background: 'var(--emerald-soft)', color: 'var(--emerald)' }}>
              <FaDollarSign size={18} />
            </div>
            <h6 className="op-metric-label mb-1">Total Net Payroll</h6>
            <h2 className="op-metric-value mb-0" style={{ color: 'var(--emerald)' }}>{formatCurrency(totalPayroll)}</h2>
            <p className="op-metric-caption mb-0 mt-2">Aggregate net disbursements</p>
          </div>
        </div>

        <div className="col-6 col-md-4 col-xl">
          <div className="op-card op-metric-card h-100 p-3 p-md-4" style={{ '--accent': 'var(--amber)' }}>
            <div className="op-metric-icon mb-3" style={{ background: 'var(--amber-soft)', color: 'var(--amber)' }}>
              <FaCoins size={18} />
            </div>
            <h6 className="op-metric-label mb-1">Avg. Net Pay / Record</h6>
            <h2 className="op-metric-value mb-0">{formatCurrency(avgNetPay)}</h2>
            <p className="op-metric-caption mb-0 mt-2">Across all processed runs</p>
          </div>
        </div>
      </div>

      {/* Chart + Snapshot Row */}
      <div className="row g-3 g-md-4 mb-4">
        <div className="col-lg-8">
          <div className="op-card h-100 p-3 p-md-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h6 className="op-chart-title mb-1">Payroll Expenses</h6>
                <p className="op-chart-subtitle mb-0">Net pay disbursed by month, {currentYear}</p>
              </div>
            </div>
            {monthlyTotals.some(v => v > 0) ? (
              <div className="op-chart-wrap">
                {monthlyTotals.map((value, i) => (
                  <div className="op-chart-col" key={MONTH_LABELS[i]}>
                    <div
                      className="op-chart-bar"
                      style={{ height: `${(value / maxMonthly) * 100}%` }}
                      title={`${MONTH_LABELS[i]}: ${formatCurrency(value)}`}
                    ></div>
                    <span className="op-chart-label">{MONTH_LABELS[i]}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="op-empty-state">No payroll disbursements recorded for {currentYear} yet.</div>
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="op-card h-100 p-3 p-md-4">
            <h6 className="op-snapshot-title mb-3">Compliance Snapshot</h6>

            <div className="op-snapshot-row">
              <div className="op-snapshot-icon"><FaHashtag size={14} /></div>
              <div className="flex-grow-1">
                <div className="op-snapshot-label">CRA Business Number</div>
                <div className="op-snapshot-value">{org?.business_number || 'Not configured'}</div>
              </div>
              <span className={`op-status-chip ${businessNumberConfigured ? 'op-status-good' : 'op-status-warn'}`}>
                {businessNumberConfigured ? <FaCheckCircle /> : <FaExclamationCircle />}
                {businessNumberConfigured ? 'Set' : 'Missing'}
              </span>
            </div>

            <div className="op-snapshot-row">
              <div className="op-snapshot-icon"><FaIndustry size={14} /></div>
              <div className="flex-grow-1">
                <div className="op-snapshot-label">Industry</div>
                <div className="op-snapshot-value">{org?.industry || 'Unspecified'}</div>
              </div>
            </div>

            <div className="op-snapshot-row">
              <div className="op-snapshot-icon"><FaEnvelope size={14} /></div>
              <div className="flex-grow-1">
                <div className="op-snapshot-label">Primary Email</div>
                <div className="op-snapshot-value">{org?.email || '—'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Profile */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="op-card">
            <div className="p-3 p-md-4" style={{ borderBottom: '1px solid var(--line)' }}>
              <h5 className="op-section-title mb-0">Business Profile</h5>
            </div>
            <div className="p-3 p-md-4">
              <div className="row g-4 g-lg-5">
                <div className="col-md-4">
                  <h6 className="op-column-title mb-3">Company Details</h6>
                  <div className="mb-3">
                    <small className="op-field-label d-block mb-1">Legal Name</small>
                    <span className="op-field-value">{org?.name || '—'}</span>
                  </div>
                  <div className="mb-3">
                    <small className="op-field-label d-block mb-1">Operating Industry</small>
                    <span className="op-field-value">{org?.industry || 'Unspecified'}</span>
                  </div>
                </div>

                <div className="col-md-4">
                  <h6 className="op-column-title mb-3">Contact Information</h6>
                  <div className="mb-3">
                    <small className="op-field-label d-block mb-1">Primary Email</small>
                    <span className="op-field-value">{org?.email || '—'}</span>
                  </div>
                  <div className="mb-3">
                    <small className="op-field-label d-block mb-1">Mailing Address</small>
                    <span className="op-field-value d-block">{org?.address || '—'}</span>
                    <span className="op-field-value d-block">
                      {org?.city ? `${org.city}, ` : ''}{org?.province || ''}{org?.postal_code ? `  ${org.postal_code}` : ''}
                    </span>
                  </div>
                </div>

                <div className="col-md-4">
                  <h6 className="op-column-title mb-3">Tax &amp; Compliance</h6>
                  <div className="mb-3">
                    <small className="op-field-label d-block mb-1">CRA Business Number</small>
                    <span className="op-field-value op-field-mono px-2 py-1 rounded d-inline-block">{org?.business_number || 'Not Configured'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Payroll Activity */}
      <div className="row">
        <div className="col-12">
          <div className="op-card p-3 p-md-4">
            <h6 className="op-snapshot-title mb-3">Recent Payroll Activity</h6>
            {recentRecords.length === 0 ? (
              <div className="op-empty-state">No payroll records to show yet.</div>
            ) : (
              <>
                {/* Table view — desktop / tablet */}
                <div className="d-none d-md-block table-responsive">
                  <table className="table op-table mb-0">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Pay Date</th>
                        <th>Gross Pay</th>
                        <th>Net Pay</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentRecords.map((r, i) => (
                        <tr key={r.id ?? i}>
                          <td className="fw-semibold">{getEmployeeName(r.employee_id)}</td>
                          <td className="op-mono-cell">{r.pay_date}</td>
                          <td className="op-mono-cell">{formatCurrency(r.gross_pay)}</td>
                          <td className="fw-semibold op-mono-cell">{formatCurrency(r.net_pay)}</td>
                          <td>
                            <span className={`op-pill ${r.status === 'Final' ? 'op-pill-final' : 'op-pill-other'}`}>
                              {r.status || 'Unknown'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Stacked card view — mobile */}
                <div className="d-md-none d-flex flex-column gap-2">
                  {recentRecords.map((r, i) => (
                    <div className="op-activity-card" key={r.id ?? i}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="fw-semibold" style={{ color: 'var(--ink)' }}>{getEmployeeName(r.employee_id)}</span>
                        <span className={`op-pill ${r.status === 'Final' ? 'op-pill-final' : 'op-pill-other'}`}>
                          {r.status || 'Unknown'}
                        </span>
                      </div>
                      <div className="op-activity-row">
                        <span className="op-activity-label">Pay Date</span>
                        <span className="op-mono-cell">{r.pay_date}</span>
                      </div>
                      <div className="op-activity-row">
                        <span className="op-activity-label">Gross Pay</span>
                        <span className="op-mono-cell">{formatCurrency(r.gross_pay)}</span>
                      </div>
                      <div className="op-activity-row">
                        <span className="op-activity-label">Net Pay</span>
                        <span className="fw-semibold op-mono-cell">{formatCurrency(r.net_pay)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit Organization Modal */}
      {showEditModal && (
        <>
          {/* Backdrop */}
          <div className="op-drawer-backdrop" onClick={closeEditModal}></div>

          {/* Sliding Panel */}
          <div
            className="op-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="op-edit-org-title"
          >
            <form onSubmit={handleSaveOrg} className="d-flex flex-column h-100">
              <div className="op-modal-header flex-shrink-0">
                <div>
                  <h5 className="op-modal-title" id="op-edit-org-title">Organization Settings</h5>
                  <p className="op-modal-subtitle mb-0">Update your company profile, contact details, and tax information.</p>
                </div>
                <button type="button" className="op-modal-close" onClick={closeEditModal} aria-label="Close">
                  <FaTimes />
                </button>
              </div>

              <div className="op-modal-body flex-grow-1">
                {saveError && <div className="op-form-error">{saveError}</div>}

                <h6 className="op-column-title mb-3">Company Details</h6>
                <div className="row g-3 mb-4">
                  <div className="col-md-8">
                    <label className="op-form-label" htmlFor="op-org-name">Legal Name</label>
                    <input id="op-org-name" type="text" className="op-form-control" value={orgForm.name} onChange={handleOrgFormChange('name')} required />
                  </div>
                  <div className="col-md-4">
                    <label className="op-form-label" htmlFor="op-org-industry">Industry</label>
                    <input id="op-org-industry" type="text" className="op-form-control" value={orgForm.industry} onChange={handleOrgFormChange('industry')} />
                  </div>
                </div>

                <h6 className="op-column-title mb-3">Contact Information</h6>
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <label className="op-form-label" htmlFor="op-org-email">Primary Email</label>
                    <input id="op-org-email" type="email" className="op-form-control" value={orgForm.email} onChange={handleOrgFormChange('email')} />
                  </div>
                  <div className="col-12">
                    <label className="op-form-label" htmlFor="op-org-address">Mailing Address</label>
                    <input id="op-org-address" type="text" className="op-form-control" value={orgForm.address} onChange={handleOrgFormChange('address')} />
                  </div>
                  <div className="col-md-6">
                    <label className="op-form-label" htmlFor="op-org-city">City</label>
                    <input id="op-org-city" type="text" className="op-form-control" value={orgForm.city} onChange={handleOrgFormChange('city')} />
                  </div>
                  <div className="col-md-3">
                    <label className="op-form-label" htmlFor="op-org-province">Province</label>
                    <input id="op-org-province" type="text" className="op-form-control" value={orgForm.province} onChange={handleOrgFormChange('province')} />
                  </div>
                  <div className="col-md-3">
                    <label className="op-form-label" htmlFor="op-org-postal">Postal Code</label>
                    <input id="op-org-postal" type="text" className="op-form-control op-form-mono" value={orgForm.postal_code} onChange={handleOrgFormChange('postal_code')} placeholder="e.g. M5V 2T6" />
                  </div>
                </div>

                <h6 className="op-column-title mb-3">Tax &amp; Compliance</h6>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="op-form-label" htmlFor="op-org-bn">CRA Business Number</label>
                    <input id="op-org-bn" type="text" className="op-form-control op-form-mono" value={orgForm.business_number} onChange={handleOrgFormChange('business_number')} placeholder="e.g. 123456789 RP0001" />
                  </div>
                </div>
              </div>

              <div className="op-modal-footer flex-shrink-0">
                <button type="button" className="btn op-btn-secondary px-4" onClick={closeEditModal} disabled={savingOrg}>
                  Cancel
                </button>
                <button type="submit" className="btn op-btn-primary px-4" disabled={savingOrg}>
                  {savingOrg ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

    </div>
  );
}

export default OrganizationDashboard;